import { computed, ref, toValue } from 'vue';
import { useStore } from '~/store';

// Reactive singleton for showNewChangeoverInterfaceSetting feature flag
// eslint-disable-next-line max-len
export const showNewChangeoverInterfaceSettingRef = ref(Boolean(window?.tenant?.showNewChangeoverInterfaceSetting?.active));

/**
 * Update the showNewChangeoverInterfaceSetting feature flag reactively
 * @param {boolean} value - The new value
 */
export function setNewChangeoverInterface(value) {
  showNewChangeoverInterfaceSettingRef.value = value;
}

export default function useChangeover(commandLaunch = null) {
  const store = useStore();

  /**
   * Tell if the given command launch targets the last changeover
   * @param commandLaunch - ref, getter or plain command launch object
   */
  const isLastChangeover = computed(
    // eslint-disable-next-line camelcase
    () => toValue(commandLaunch)?.cl_changeover_expand?.is_last,
  );

  /**
   * Check if there is a running changeover
   */
  const hasRunningChangeovers = computed(() => store.state.changeovers.runningChangeover !== null);

  /**
   * Selected changeover id in the navigation
   */
  const selectedChangeoverId = computed({
    get() {
      return store.state.navigation.selectedChangeover;
    },
    set(value) {
      store.commit('navigation/setSelectedChangeover', value);
    },
  });

  /**
   * Selected changeover in the navigation
   */
  const selectedChangeover = computed(() => {
    const { changeovers } = store.state.navigation;
    if (Array.isArray(changeovers)) {
      return changeovers.find((ch) => ch.changeover_id === selectedChangeoverId.value);
    }
    return undefined;
  });

  /**
   * Selected changeover has not command
   */
  // eslint-disable-next-line max-len
  const changeoverHasNoCommand = computed(() => selectedChangeover.value?.changeover_cl_trigger === null);

  /**
   * Identity of the selected changeover published by the nav-next OF row, which
   * does not fill the legacy `changeovers` list. Only trusted while it still
   * designates the current selection (it is the last OF the navigation applied).
   */
  const selectedChangeoverSummary = computed(() => {
    const summary = store.getters['navigation/selectedChangeoverSummary'] ?? null;
    if (!summary || !selectedChangeoverId.value) return null;

    return String(summary.changeoverId) === String(selectedChangeoverId.value) ? summary : null;
  });

  /**
   * Tell if a changeover is selected in the navigation — whichever navigation
   * published it (the legacy list, or the nav-next descriptor).
   * @returns {boolean}
   */
  const hasSelectedChangeover = computed(
    () => Boolean(selectedChangeover.value || selectedChangeoverSummary.value),
  );

  /**
   * Actual phase selected
   * @returns {*}
   */
  const selectedPhase = computed(() => selectedChangeover.value?.phaseExpand ?? null);

  /**
   * Status of the selected phase — from the loaded changeover in the legacy
   * navigation, from the nav-next descriptor otherwise.
   * @returns {?string}
   */
  const selectedPhaseStatus = computed(
    () => selectedPhase.value?.status ?? selectedChangeoverSummary.value?.phaseStatus ?? null,
  );

  /**
   * Event id of the selected changeover: the key the job wizard self-loads the
   * whole job from (GET_CHANGEOVER_FOR_WIZARD). 0 = nothing to load.
   * @returns {string|number}
   */
  const selectedChangeoverEventId = computed(
    () => selectedChangeover.value?.changeover_event
      ?? selectedChangeoverSummary.value?.eventId
      ?? 0,
  );

  /**
   * Actual job selected
   * @returns {*}
   */
  const selectedJob = computed(() => selectedPhase.value?.jobExpand ?? null);

  /**
   * Tell if the selected job is in progress
   * @returns {boolean}
   */
  const isJobInProgress = computed(() => !!(
    selectedPhase.value
      && selectedJob.value
      && (selectedPhase.value.status.toUpperCase() === 'LAUNCHED'
       || selectedPhase.value.status.toUpperCase() === 'PAUSED')
  ));

  /**
   * Phase for close job dialog
   * @returns {{reject, good}}
   */
  const closeJobPhaseDetails = computed(() => ({
    good: selectedChangeover.value?.jobProgressExpand.good,
    reject: selectedChangeover.value?.jobProgressExpand.reject,
    ...selectedPhase.value,
  }));

  /**
   * selected manufacturing order code
   * @returns {string|*}
   */
  const selectedManufacturingOrderCode = computed(
    () => selectedChangeover.value?.changeover_mo_expand?.mo_code
      ?? selectedChangeoverSummary.value?.moCode,
  );

  /**
   * Commit if the modal running changeover is open/close
   */
  const commitIsModalRunningChangeoverOpen = (isOpen) => {
    store.commit('changeovers/commitIsModalRunningChangeoverOpen', isOpen);
  };

  return {
    changeoverHasNoCommand,
    closeJobPhaseDetails,
    commitIsModalRunningChangeoverOpen,
    hasRunningChangeovers,
    hasSelectedChangeover,
    isJobInProgress,
    isLastChangeover,
    selectedChangeover,
    selectedChangeoverEventId,
    selectedJob,
    selectedManufacturingOrderCode,
    selectedPhase,
    selectedPhaseStatus,
    showNewChangeoverInterfaceSettingRef,
  };
}
